"use client";

import EditableList from "@/app/(components)/generics/EditableList/EditableList";
import EditableText from "@/app/(components)/generics/EditableText/EditableText";
import IconArrowRightGradient from "@/app/(components)/generics/Icons/IconArrowRightGradient";
import TabbedContent from "@/app/(components)/generics/TabbedContent/TabbedContent";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { ProjectOfferData } from "@/app/types/client-avatar/UserClientAvatar";
import { Globals } from "@/app/vars/Globals";
import { Limits } from "@/app/vars/Limits";
import styles from "./OfferDetail.module.css";

type Props = {
  offer: ProjectOfferData;
  lang: string;
  canEdit: boolean;
  onSave: (patch: Partial<ProjectOfferData>) => void;
};

export default function OfferDetail(props: Props) {
  const T = useT();
  const { offer, lang, canEdit, onSave } = props;

  const showType = canEdit || !!offer.type;
  const showPrice = canEdit || !!offer.price;
  const showProblem = canEdit || !!offer.problem;
  const showPromise = canEdit || !!offer.promise;
  const showTransform = showProblem || showPromise;
  const showArrow = showProblem && showPromise;
  const showProof = canEdit || offer.proof.length > 0;
  const showAdvantages = canEdit || offer.keyAdvantages.length > 0;

  const tabItems = [
    showProof
      ? {
          id: TKey.OFFER_PROOF_LABEL,
          title: T(TKey.OFFER_PROOF_LABEL) as string,
          content: (
            <div className={styles.tabPanel}>
              <EditableList
                items={offer.proof}
                onSave={(items) => onSave({ proof: items })}
                addItemText={T(TKey.OFFER_PROOF_ADD) as string}
                placeholder={T(TKey.OFFER_PROOF_PLACEHOLDER) as string}
                maxItems={Globals.MAX_OFFER_LIST_ITEMS}
                itemMaxLength={Limits.MAX_OFFER_LIST_ITEM}
                disabled={!canEdit}
                lang={lang}
              />
            </div>
          ),
        }
      : null,
    showAdvantages
      ? {
          id: TKey.OFFER_KEY_ADVANTAGES_LABEL,
          title: T(TKey.OFFER_KEY_ADVANTAGES_LABEL) as string,
          content: (
            <div className={styles.tabPanel}>
              <EditableList
                items={offer.keyAdvantages}
                onSave={(items) => onSave({ keyAdvantages: items })}
                addItemText={T(TKey.OFFER_KEY_ADVANTAGES_ADD) as string}
                placeholder={T(TKey.OFFER_KEY_ADVANTAGES_PLACEHOLDER) as string}
                maxItems={Globals.MAX_OFFER_LIST_ITEMS}
                itemMaxLength={Limits.MAX_OFFER_LIST_ITEM}
                disabled={!canEdit}
                lang={lang}
              />
            </div>
          ),
        }
      : null,
  ].filter((item) => item !== null);

  return (
    <div className={styles.container}>
      {(showType || showPrice) && (
        <div className={styles.meta}>
          {showType && (
            <EditableText
              value={offer.type || ""}
              onSave={(val) => onSave({ type: val || null })}
              placeholder={T(TKey.OFFER_TYPE_LABEL) as string}
              disabled={!canEdit}
              fitContent={true}
              alignCenter={true}
              maxLength={Limits.MAX_OFFER_TYPE}
              className={styles.chip}
              textClassName={styles.chipText}
            />
          )}
          {showPrice && (
            <EditableText
              value={offer.price || ""}
              onSave={(val) => onSave({ price: val || null })}
              placeholder={T(TKey.OFFER_PRICE_LABEL) as string}
              disabled={!canEdit}
              fitContent={true}
              alignCenter={true}
              maxLength={Limits.MAX_OFFER_PRICE}
              className={`${styles.chip} ${styles.chipPrice}`}
              textClassName={styles.chipPriceText}
            />
          )}
        </div>
      )}

      {showTransform && (
        <div className={styles.transform}>
          {showProblem && (
            <div className={styles.block}>
              <span className={styles.blockLabel}>{T(TKey.OFFER_PROBLEM_LABEL)}</span>
              <EditableText
                value={offer.problem || ""}
                onSave={(val) => onSave({ problem: val || null })}
                placeholder={T(TKey.OFFER_PROBLEM_PLACEHOLDER) as string}
                multiline={true}
                maxLength={Limits.MAX_OFFER_PROBLEM}
                disabled={!canEdit}
                textClassName={styles.blockText}
              />
            </div>
          )}

          {showArrow && (
            <div className={styles.arrow}>
              <IconArrowRightGradient size={18} />
            </div>
          )}

          {showPromise && (
            <div className={`${styles.block} ${styles.blockPromise}`}>
              <span className={styles.blockLabel}>{T(TKey.OFFER_PROMISE_LABEL)}</span>
              <EditableText
                value={offer.promise || ""}
                onSave={(val) => onSave({ promise: val || null })}
                placeholder={T(TKey.OFFER_PROMISE_PLACEHOLDER) as string}
                multiline={true}
                maxLength={Limits.MAX_OFFER_PROMISE}
                disabled={!canEdit}
                textClassName={styles.blockTextPromise}
              />
            </div>
          )}
        </div>
      )}

      {tabItems.length > 0 && (
        <div className={styles.tabs}>
          <TabbedContent
            items={tabItems}
            hideContentPadding={true}
            disableScrollOnTabChange={true}
          />
        </div>
      )}
    </div>
  );
}
